import { useCallback, useEffect, useRef, useState } from 'react';
import { useLogWsMessage } from '../hooks/LogWsProvider';
import { useClusterLive } from '../hooks/useClusterLive';
import { isLiveStatus } from '../features/clusters';
import type { PfTask } from '../domain/positions';
import { ST } from '../lib/icons';
import { useApp, type TaskStats } from '../store/AppContext';

export function FarmStatus() {
  const { clusters, controlMode, taskStats, setTaskStats, refreshTasks } = useApp();
  useClusterLive();

  useEffect(() => {
    void refreshTasks().catch(() => undefined);
  }, [refreshTasks]);

  useLogWsMessage('tasks_live', (msg) => {
    if (msg.stats && typeof msg.stats === 'object') setTaskStats(msg.stats as TaskStats);
  });

  const online = clusters.filter(isLiveStatus).length;
  const total = clusters.length;
  const tone = !total ? 'idle' : online === total ? 'ok' : online ? 'warn' : 'err';
  const active = taskStats?.active ?? 0;

  return (
    <div className="ab-status">
      {controlMode === 'control' ? (
        <span className="ab-stat" title="Ноды онлайн / всего">
          <i className={`ab-dot ${tone}`} />
          Ноды <b>{total ? `${online}/${total}` : '—'}</b>
        </span>
      ) : null}
      <span className="ab-stat" title="Задачи со статусом «Выполняется»">
        <i className={`ab-dot ${active ? 'ok' : 'idle'}`} />
        Задач в процессе <b>{active}</b>
      </span>
    </div>
  );
}

type Note = { id: number; kind: 'error' | 'task'; text: string; t: number };

const MAX_NOTES = 30;

export function NotificationBell() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);
  const statuses = useRef<Map<string, string> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const push = useCallback((kind: Note['kind'], text: string) => {
    seq.current += 1;
    const note: Note = { id: seq.current, kind, text, t: Date.now() };
    setNotes((prev) => [note, ...prev].slice(0, MAX_NOTES));
    setUnread((n) => n + 1);
  }, []);

  useLogWsMessage('log', (msg) => {
    const entry = msg.entry as { lvl?: string; text?: string; msg?: string } | undefined;
    if (entry?.lvl !== 'er') return;
    const source = typeof msg.cluster === 'string' ? msg.cluster : typeof msg.source === 'string' ? msg.source : '';
    const text = entry.text || entry.msg || 'Ошибка';
    push('error', source && source !== 'control' ? `${source}: ${text}` : text);
  });

  useLogWsMessage('tasks_live', (msg) => {
    if (!Array.isArray(msg.tasks)) return;
    const tasks = msg.tasks as PfTask[];
    const prev = statuses.current;
    statuses.current = new Map(tasks.map((t) => [t.id, t.status]));
    if (!prev) return;
    for (const t of tasks) {
      if (prev.get(t.id) === 'run' && t.status !== 'run') {
        push('task', `${t.site}: ${ST[t.status]?.[1] ?? t.status}`);
      }
    }
  });

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev: MouseEvent) => {
      if (ev.target instanceof Node && rootRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    setOpen((v) => !v);
    setUnread(0);
  };

  return (
    <div className="bell" ref={rootRef}>
      <button
        type="button"
        className="ab-icon"
        title="Уведомления"
        aria-label={unread ? `Уведомления: ${unread} новых` : 'Уведомления'}
        aria-expanded={open}
        onClick={toggle}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6.5 2 6.5H4S6 14 6 9Z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
        {unread ? <span className="bell-badge">{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      {open ? (
        <div className="bell-panel" role="dialog" aria-label="Уведомления">
          <div className="bell-head">
            <b>Уведомления</b>
            {notes.length ? (
              <button type="button" className="bell-clear" onClick={() => setNotes([])}>
                Очистить
              </button>
            ) : null}
          </div>
          {notes.length ? (
            <ul className="bell-list">
              {notes.map((n) => (
                <li key={n.id} className={`bell-item ${n.kind}`}>
                  <div className="bell-meta">
                    <span className="bell-kind">{n.kind === 'error' ? 'Ошибка' : 'Задача'}</span>
                    <time>{new Date(n.t).toLocaleTimeString('ru-RU')}</time>
                  </div>
                  <div className="bell-text">{n.text}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="bell-empty">Пока пусто</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
